import { Table, TableBody, TableCell, TableHeader, TableRow } from "../ui/table";
import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import { ChevronDownIcon, CloseIcon, ChevronLeftIcon } from "../../icons";
import { StarIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { RequestStatus, DetailedGeneralRequest } from "../../types/request";
import RequestsTab from "../common/RequestsTab";
import Alert from "../ui/alert/Alert";

import { LoadingSpinner } from "../ui/LoadingSpinner";
import { PageLoadingView } from "../common/PageLoadingView";
import SimpleDate from "../common/SimpleDate";
import { useGeneralRequests } from "../../hooks/useGeneralRequests";
import { useMessageTypes } from "../../hooks/useMessageTypes";
import { useUser } from "../../context/UserInfoProvider";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useHotelInfo } from "../../hooks/useHotelInfo";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { useRequestRefresh } from "../../hooks/useRequestRefresh";
import ConfirmationDialog from "../common/ConfirmationDialog";
import MessageHistorySidebar from "../common/MessageHistorySidebar";
import { RequestsToolbar } from "../common/RequestsToolbar";

// Helper to display nullable fields
const displayValue = (
  value: string | number | null | undefined,
  defaultValue: string = "N/A",
) => {
  return value !== null && value !== undefined && String(value).trim() !== ""
    ? String(value)
    : defaultValue;
};

// Helper to format date with timezone support
const formatDateWithTimezone = (
  dateString: string,
  timezone?: string | null,
) => {
  const date = new Date(dateString);

  if (timezone) {
    // Use the hotel's timezone
    return date.toLocaleString("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } else {
    // Fallback to local timezone
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
};

export default function GeneralReqs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { callApi } = useAuthenticatedApi();
  const { platformUserId } = useUser();
  const analytics = useAnalytics();
  const { data: hotelInfo } = useHotelInfo();

  const [selectedSort, setSelectedSort] = useState("Time");
  const [selectedFrame, setSelectedFrame] = useState("Active");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedRequest, setSelectedRequest] =
    useState<DetailedGeneralRequest | null>(null);
  const [liveStatus, setLiveStatus] = useState("");
  const [isPushingStatus, setIsPushingStatus] = useState(false);
  const [subject, setSubject] = useState("");
  const [showPushAlert, setShowPushAlert] = useState(false);
  const [showStatusUpdateAlert, setShowStatusUpdateAlert] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isSidebarStatusDropdownOpen, setIsSidebarStatusDropdownOpen] =
    useState(false);
  const [starredList, setStarredList] = useState<Set<string>>(new Set());
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isCategoryFilterDropdownOpen, setIsCategoryFilterDropdownOpen] =
    useState(false);
  const [showStarredOnly, setShowStarredOnly] = useState(false);

  // Track which status dropdowns are open
  const [statusDropdowns, setStatusDropdowns] = useState<{
    [key: string]: boolean;
  }>({});

  // Track which items are being updated
  const [updatingStatus, setUpdatingStatus] = useState<{
    [key: string]: boolean;
  }>({});

  // Refs for status dropdown buttons
  const statusButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>(
    {},
  );

  // Local search term state for UI
  const [searchTermLocal, setSearchTermLocal] = useState<string>("");

  // Determine API status filter based on selected frame and status filter
  const apiStatusFilter = useMemo(() => {
    // If a specific status is selected (not "All"), use only that status
    if (selectedStatus !== "All") {
      const statusMap: Record<string, string> = {
        Submitted: "Submitted",
        "In Progress": "InProgress",
        Delayed: "Delayed",
        Scheduled: "Scheduled",
        Completed: "Completed",
        Cancelled: "Cancelled",
      };
      const apiStatus = statusMap[selectedStatus];
      return apiStatus ? [apiStatus as RequestStatus] : undefined;
    }

    // Otherwise, use frame-based filtering
    if (selectedFrame === "Active") {
      return [
        "Submitted",
        "InProgress",
        "Delayed",
      ] as unknown as RequestStatus[];
    } else if (selectedFrame === "Scheduled") {
      return ["Scheduled"] as unknown as RequestStatus[];
    } else if (selectedFrame === "History") {
      return ["Completed", "Cancelled"] as unknown as RequestStatus[];
    }
    return undefined;
  }, [selectedFrame, selectedStatus]);

  // Use the general requests hook
  const {
    requests: fetchedRequests,
    isLoading,
    error,
    updateRequestStatus,
    fetchRequestById,
    pagination,
    setPage,
    setLimit,
    setSearchTerm,
    refetch,
  } = useGeneralRequests({
    hotelId: hotelInfo?.hotelid || "",
    initialPage: 1,
    initialLimit: 10,
    status: apiStatusFilter,
    category: selectedCategory !== "All" ? selectedCategory : undefined,
    selectedFrame,
    sortBy: selectedSort === "Time" ? "createdAt" : "requestId",
    sortOrder: "desc",
  });

  // Setup auto-refresh when navigating to the page and when new requests come in
  useRequestRefresh({
    onRefresh: () => {
      refetch();
    },
    requestType: "general",
  });

  // Persist favorites locally (scoped by user and page)
  const favoritesStorageKey = useMemo(() => {
    const uid = platformUserId || "anon";
    return `selfserve:favorites:general:${uid}`;
  }, [platformUserId]);

  const [hasLoadedFavorites, setHasLoadedFavorites] = useState(false);

  // Reset loaded flag when key changes to prevent writing stale data under new key
  useEffect(() => {
    setHasLoadedFavorites(false);
  }, [favoritesStorageKey]);

  // Load favorites on mount / when user changes
  useEffect(() => {
    try {
      const raw = localStorage.getItem(favoritesStorageKey);
      if (raw) {
        const ids: string[] = JSON.parse(raw);
        if (Array.isArray(ids)) {
          setStarredList(new Set(ids));
        }
      } else {
        setStarredList(new Set());
      }
    } catch {
      setStarredList(new Set());
    }
    setHasLoadedFavorites(true);
  }, [favoritesStorageKey]);

  // Save favorites whenever it changes
  useEffect(() => {
    if (!hasLoadedFavorites) return;
    try {
      const ids = Array.from(starredList);
      localStorage.setItem(favoritesStorageKey, JSON.stringify(ids));
    } catch {
      // no-op
    }
  }, [starredList, favoritesStorageKey, hasLoadedFavorites]);

  // Use the message types hook
  const { messageTypes, isLoadingMessageTypes, messageTypeError } =
    useMessageTypes();

  // UI-specific state for message types
  const [selectedMessageTypeId, setSelectedMessageTypeId] =
    useState<string>("");

  // Message confirmation and history states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<{
    subject: string;
    content: string;
    typeId: string;
  } | null>(null);
  const [showMessageHistory, setShowMessageHistory] = useState(false);

  // Track navigation state
  const [pendingNavigation, setPendingNavigation] = useState<
    "previous" | "next" | null
  >(null);

  // Cast fetchedRequests to DetailedGeneralRequest[]
  const generalRequests = fetchedRequests as DetailedGeneralRequest[];

  // Auto-select first message type when loaded
  useEffect(() => {
    if (messageTypes.length > 0 && !selectedMessageTypeId) {
      setSelectedMessageTypeId(messageTypes[0].typeid);
    }
  }, [messageTypes, selectedMessageTypeId]);

  const handleSelectRequest = async (req: DetailedGeneralRequest) => {
    try {
      const completeRequest = await fetchRequestById(req.requestId);
      setSelectedRequest(completeRequest);
      setLiveStatus("");
      setShowPushAlert(false);
      setStatusError(null);
      // Sync URL for deep linking (do not auto-open message history)
      try {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("requestId", req.requestId);
          return next;
        });
      } catch {
        // Ignore URL update errors
      }
    } catch (err) {
      const error = err as Error;
      setStatusError(error.message || "Failed to load request details");
    }
  };

  const handleSidebarStatusChange = async (newStatus: RequestStatus) => {
    if (!selectedRequest || isUpdatingStatus) return;

    setIsUpdatingStatus(true);
    setStatusError(null);
    setIsSidebarStatusDropdownOpen(false);

    try {
      let apiStatus: string = newStatus;
      if (newStatus === "IN_PROGRESS") apiStatus = "InProgress";
      else if (newStatus === "SUBMITTED") apiStatus = "Submitted";
      else if (newStatus === "DELAYED") apiStatus = "Delayed";
      else if (newStatus === "SCHEDULED") apiStatus = "Scheduled";
      else if (newStatus === "COMPLETED") apiStatus = "Completed";
      else if (newStatus === "CANCELLED") apiStatus = "Cancelled";

      const updatePayload: {
        requestId: string;
        status: RequestStatus;
        completedAt?: string;
      } = {
        requestId: selectedRequest.requestId,
        status: apiStatus as RequestStatus,
      };

      if (apiStatus === "Completed") {
        updatePayload.completedAt = new Date().toISOString();
      }

      await updateRequestStatus({
        requestId: selectedRequest.requestId,
        status: apiStatus as RequestStatus,
        hotelId: hotelInfo?.hotelid || "",
      });

      setSelectedRequest((prev) =>
        prev
          ? {
              ...prev,
              status: apiStatus as RequestStatus,
              completedAt:
                apiStatus === "Completed"
                  ? updatePayload.completedAt!
                  : prev.completedAt || null,
              updatedAt: new Date().toISOString(),
            }
          : null,
      );

      // Track request status change analytics
      analytics.request.statusChange(
        "general",
        apiStatus.toLowerCase(),
        selectedRequest.status?.toLowerCase(),
      );

      // Track completion analytics
      if (apiStatus === "Completed") {
        analytics.request.process("general", "completed");
      }

      // Wait for refetch to complete before showing toast
      await refetch();
      setShowStatusUpdateAlert(true);
      setTimeout(() => setShowStatusUpdateAlert(false), 3000);

      // Don't change the selected frame (tab) when updating status from sidebar
      // User should remain on their current tab
    } catch (err) {
      const error = err as Error;
      setStatusError(
        error.message || "Failed to update status. Please try again.",
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handlePushToGuest = async () => {
    if (!liveStatus.trim()) {
      setStatusError("Message content is required");
      return;
    }
    if (!selectedMessageTypeId) {
      setStatusError("Please select a message type.");
      return;
    }
    if (!selectedRequest) {
      setStatusError("No request selected.");
      return;
    }
    if (!platformUserId) {
      setStatusError(
        "Sender User ID (platformUserId) is missing or not configured.",
      );
      return;
    }
    if (!selectedRequest.platformUserId) {
      setStatusError(
        "Receiver (guest) platform user ID (platformUserId) is missing for this request.",
      );
      console.error(
        "Receiver platformUserId missing on selectedRequest:",
        selectedRequest,
      );
      return;
    }

    // Show confirmation dialog instead of sending immediately
    setPendingMessage({
      subject: subject || "",
      content: liveStatus,
      typeId: selectedMessageTypeId,
    });
    setShowConfirmDialog(true);
  };

  const handleConfirmSend = async () => {
    if (!pendingMessage || !selectedRequest) return;

    setIsPushingStatus(true);
    setStatusError(null);
    setShowConfirmDialog(false);

    try {
      await callApi({
        method: "POST",
        url: "/management/messages",
        data: {
          senderPlatformUserId: platformUserId,
          receiverPlatformUserId: selectedRequest.platformUserId,
          typeId: pendingMessage.typeId,
          subject: pendingMessage.subject,
          content: pendingMessage.content,
          requestId: selectedRequest.requestId,
        },
      });

      setLiveStatus("");
      setSubject("");
      setPendingMessage(null);
      setShowPushAlert(true);
      setTimeout(() => setShowPushAlert(false), 3000);

      // Track guest communication analytics
      analytics.staff("message_sent_to_guest", {
        request_type: "general",
        message_length: pendingMessage.content.length,
        has_subject: !!pendingMessage.subject,
        recipient_type: "guest",
      });
    } catch (err) {
      const error = err as Error;
      setStatusError(error.message || "Failed to send message to guest");
    } finally {
      setIsPushingStatus(false);
    }
  };

  const handleCancelSend = () => {
    setShowConfirmDialog(false);
    setPendingMessage(null);
  };

  const toggleStars = (requestId: string) => {
    setStarredList((prev) => {
      const updatedSet = new Set(prev);
      if (updatedSet.has(requestId)) {
        updatedSet.delete(requestId);
      } else {
        updatedSet.add(requestId);
      }
      return updatedSet;
    });
  };

  const toggleFilterDropdown = () =>
    setIsFilterDropdownOpen(!isFilterDropdownOpen);
  const toggleSortDropdown = () => setIsSortDropdownOpen(!isSortDropdownOpen);

  const toggleStatusDropdown = (requestId: string) => {
    setStatusDropdowns((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (key !== requestId) {
          newState[key] = false;
        }
      });
      newState[requestId] = !newState[requestId];
      return newState;
    });
  };

  const handleTableStatusChange = async (
    requestId: string,
    newStatus: string,
  ) => {
    try {
      setUpdatingStatus((prev) => ({ ...prev, [requestId]: true }));
      setStatusDropdowns((prev) => ({ ...prev, [requestId]: false }));

      let apiStatus: string = newStatus;
      if (newStatus === "In Progress") apiStatus = "InProgress";
      else if (newStatus === "Submitted") apiStatus = "Submitted";
      else if (newStatus === "Delayed") apiStatus = "Delayed";
      else if (newStatus === "Scheduled") apiStatus = "Scheduled";
      else if (newStatus === "Completed") apiStatus = "Completed";
      else if (newStatus === "Cancelled") apiStatus = "Cancelled";

      const updatePayload: {
        requestId: string;
        status: RequestStatus;
        completedAt?: string;
      } = {
        requestId,
        status: apiStatus as RequestStatus,
      };

      if (apiStatus === "Completed") {
        updatePayload.completedAt = new Date().toISOString();
      }

      await updateRequestStatus({
        requestId,
        status: apiStatus as RequestStatus,
        hotelId: hotelInfo?.hotelid || "",
      });

      // Track request status change analytics
      analytics.request.statusChange("general", apiStatus.toLowerCase(), "");

      // Wait for refetch to complete before showing toast
      await refetch();
      setShowStatusUpdateAlert(true);
      setTimeout(() => setShowStatusUpdateAlert(false), 3000);

      // Don't change the selected frame (tab) when updating status from table
      // User should remain on their current tab
    } catch (err) {
      const error = err as Error;
      setStatusError(
        error.message || "Failed to update status. Please try again.",
      );
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [requestId]: false }));
      setStatusDropdowns((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const getFrameClass = () => "text-[#1100D8] font-[300] dark:text-gray-400";

  const getRowClassName = (status: string) => {
    if (status === "COMPLETED") {
      return "bg-gray-50 dark:bg-transparent";
    }
    return "";
  };

  const getStatusDisplay = (status: RequestStatus) => {
    if ((status as string) === "InProgress") return "In Progress";
    return status
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getStatusOptions = () => {
    return [
      "Submitted",
      "In Progress",
      "Delayed",
      "Scheduled",
      "Completed",
      "Cancelled",
    ];
  };

  const getDepartmentDisplay = (department: string | null | undefined) => {
    return displayValue(department);
  };

  const getFilterOptions = (frame: string) => {
    switch (frame) {
      case "Active":
        return ["All", "Submitted", "In Progress", "Delayed"];
      case "History":
        return ["All", "Completed", "Cancelled"];
      default:
        return [];
    }
  };

  // Extract unique categories from the fetched requests
  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    generalRequests.forEach((request) => {
      if (request.requestCategory && request.requestCategory.trim() !== "") {
        categories.add(request.requestCategory);
      }
    });
    return Array.from(categories).sort();
  }, [generalRequests]);

  const toggleCategoryFilterDropdown = () =>
    setIsCategoryFilterDropdownOpen(!isCategoryFilterDropdownOpen);

  // const normalizeStatus = (status: string): string => { // Unused function removed
  //   return status.toUpperCase().replace(/[_\s]/g, "");
  // };

  // Remove frontend filtering - backend handles search and status filtering
  const filteredData = useMemo(() => {
    return (generalRequests || []).filter((request) => {
      const passesStarredFilter = showStarredOnly
        ? starredList.has(request.requestId)
        : true;

      return passesStarredFilter;
    });
  }, [generalRequests, showStarredOnly, starredList]);

  const sortedData = useMemo(() => {
    // Sorting handled by backend; keep current page order
    return filteredData;
  }, [filteredData]);

  // No client-side pagination needed - backend handles pagination
  const paginatedData = sortedData;

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const hasMore = pagination.page < totalPages;

  // Reset pagination when switching tabs or filters
  useEffect(() => {
    setPage(1);
  }, [selectedFrame, selectedStatus, selectedCategory, setPage]);

  // Initialize selected tab from statusTab param
  useEffect(() => {
    const statusTab = new URLSearchParams(window.location.search).get(
      "statusTab",
    );
    if (statusTab && ["Active", "Scheduled", "History"].includes(statusTab)) {
      setSelectedFrame(statusTab);
    }
  }, []);

  // Auto-select from URL deep link if requestId is present (do not auto-open message history)
  const [didInitDeepLink, setDidInitDeepLink] = useState(false);

  useEffect(() => {
    if (didInitDeepLink) return;
    const reqId = searchParams.get("requestId");
    if (!reqId) return;

    // If already selected, mark as done
    if (selectedRequest && selectedRequest.requestId === reqId) {
      setDidInitDeepLink(true);
      return;
    }

    // Check if the request exists in the current page's data
    const match = sortedData.find((r) => r.requestId === reqId);

    if (match) {
      // Request found in current page - select it
      handleSelectRequest(match);
      setDidInitDeepLink(true);
    } else if (!isLoading) {
      // Request not in current page - fetch it directly by ID
      const fetchAndSelectRequest = async () => {
        try {
          const completeRequest = await fetchRequestById(reqId);
          setSelectedRequest(completeRequest);
          setDidInitDeepLink(true);
        } catch (error) {
          console.error("Failed to fetch deep-linked request:", error);
          setDidInitDeepLink(true); // Mark as done even on error to prevent infinite loop
        }
      };
      fetchAndSelectRequest();
    }
  }, [
    searchParams,
    selectedRequest,
    sortedData,
    didInitDeepLink,
    isLoading,
    fetchRequestById,
  ]);

  // Handle navigation across pages - clear sidebar when navigating to new page
  useEffect(() => {
    if (pendingNavigation) {
      setSelectedRequest(null);
    }
  }, [pendingNavigation]);

  // Handle navigation across pages - select appropriate item after page loads
  useEffect(() => {
    if (pendingNavigation && sortedData.length > 0) {
      // Small delay to ensure data is loaded
      const timer = setTimeout(() => {
        if (pendingNavigation === "previous") {
          // Select the last item of the new page
          const lastItem = sortedData[sortedData.length - 1];
          if (lastItem) {
            handleSelectRequest(lastItem);
          }
        } else if (pendingNavigation === "next") {
          // Select the first item of the new page
          const firstItem = sortedData[0];
          if (firstItem) {
            handleSelectRequest(firstItem);
          }
        }
        setPendingNavigation(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingNavigation, sortedData, handleSelectRequest]);

  if (isLoading) {
    return <PageLoadingView />;
  }

  if (error) {
    return (
      <div className="flex flex-col h-full p-6 items-center justify-center">
        <Alert
          variant="error"
          title="Error Loading Requests"
          message={error.message || "Could not load requests."}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header + Subheader */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-white mb-2">
          General Requests
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Keep track of all your guest's general requests on this dashboard.
        </p>
      </div>

      {/* Error Alert */}
      {statusError && (
        <div className="mb-4">
          <Alert variant="error" title="Error" message={statusError} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between mb-4">
        <RequestsTab
          onFrameChange={setSelectedFrame}
          selectedFrame={selectedFrame}
          options={["Active", "Scheduled", "History"]}
        />
      </div>

      {/* Refresh + Search + Filter + Sort + Favorites */}
      <RequestsToolbar
        searchQuery={searchTermLocal}
        onSearchChange={(value) => {
          setSearchTermLocal(value);
          if (value === "") {
            setSearchTerm("");
          }
        }}
        onSearchSubmit={() => {
          setSearchTerm(searchTermLocal);
          setPage(1); // Reset to first page when searching
        }}
        isLoading={isLoading}
        onRefresh={() => window.location.reload()}
      >
        <>
          {selectedFrame !== "Scheduled" && (
            <>
              {selectedStatus !== "All" && (
                <div className="bg-white border border-gray-100 rounded-lg dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]">
                  <button
                    className="text-black"
                    onClick={() => {
                      setSelectedStatus("All");
                    }}
                  >
                    <div className="flex w-full h-full mx-2 items-center gap-2 font-light text-sm px-2 py-2">
                      {selectedStatus}
                      <CloseIcon />
                    </div>
                  </button>
                </div>
              )}
            </>
          )}
          {selectedCategory !== "All" && (
            <div className="bg-white border border-gray-100 rounded-lg dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]">
              <button
                className="text-black"
                onClick={() => {
                  setSelectedCategory("All");
                }}
              >
                <div className="flex w-full h-full mx-2 items-center gap-2 font-light text-sm px-2 py-2">
                  {selectedCategory}
                  <CloseIcon />
                </div>
              </button>
            </div>
          )}
          {selectedFrame !== "Scheduled" && (
            <div className="relative inline-block w-full md:w-56">
              <button
                className="flex dropdown-toggle w-full items-center justify-between 
                px-4 py-2 text-sm text-black bg-white border rounded-md
                hover:bg-gray-100 dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]"
                onClick={toggleFilterDropdown}
              >
                <span className="px-4">Filter by Status</span>
                <ChevronDownIcon className="w-4 h-4 text-black dark:text-white/90" />
              </button>
              <Dropdown
                isOpen={isFilterDropdownOpen}
                onClose={toggleFilterDropdown}
                className="w-full absolute z-100"
              >
                {getFilterOptions(selectedFrame).map((option) => (
                  <DropdownItem
                    className="dark:text-white/90"
                    key={option}
                    onItemClick={() => {
                      setSelectedStatus(option);
                      toggleFilterDropdown();
                    }}
                  >
                    {option}
                  </DropdownItem>
                ))}
              </Dropdown>
            </div>
          )}
          <div className="relative inline-block w-full md:w-56">
            <button
              className="flex dropdown-toggle w-full items-center justify-between 
              px-4 py-2 text-sm text-black bg-white border rounded-md
              hover:bg-gray-100 dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]"
              onClick={toggleCategoryFilterDropdown}
            >
              <span className="px-4">Filter by Category</span>
              <ChevronDownIcon className="w-4 h-4 text-black dark:text-white/90" />
            </button>
            <Dropdown
              isOpen={isCategoryFilterDropdownOpen}
              onClose={toggleCategoryFilterDropdown}
              className="w-full absolute z-100"
            >
              <DropdownItem
                className="dark:text-white/90"
                onItemClick={() => {
                  setSelectedCategory("All");
                  toggleCategoryFilterDropdown();
                }}
              >
                All
              </DropdownItem>
              {availableCategories.map((category) => (
                <DropdownItem
                  className="dark:text-white/90"
                  key={category}
                  onItemClick={() => {
                    setSelectedCategory(category);
                    toggleCategoryFilterDropdown();
                  }}
                >
                  {category}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
          <div className="relative inline-block w-full md:w-56">
            <button
              className="flex dropdown-toggle w-full items-center justify-between 
              px-4 py-2 text-sm text-black bg-white border rounded-md
              hover:bg-gray-100 dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]"
              onClick={toggleSortDropdown}
            >
              <span className="px-4">{`Sort By ${selectedSort}`}</span>
              <ChevronDownIcon className="w-4 h-4 text-black dark:text-white/90" />
            </button>
            <Dropdown
              isOpen={isSortDropdownOpen}
              onClose={toggleSortDropdown}
              className="w-full absolute z-100"
            >
              {["Time", "Request ID"].map((option) => (
                <DropdownItem
                  className="dark:text-white/90"
                  key={option}
                  onItemClick={() => {
                    setSelectedSort(option);
                    setPage(1);
                    toggleSortDropdown();
                  }}
                >
                  {`Sort By ${option}`}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
          <button
            className={`flex items-center justify-center px-4 py-2 text-sm border rounded-md
              ${
                showStarredOnly
                  ? "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800"
                  : "bg-white text-black border-gray-200 dark:bg-white/[0.03] dark:text-white/90 dark:border-gray-800"
              }`}
            onClick={() => {
              setShowStarredOnly(!showStarredOnly);
              setPage(1);
            }}
          >
            {showStarredOnly ? (
              <StarIconSolid className="w-5 h-5" />
            ) : (
              <StarIcon className="w-5 h-5" />
            )}
          </button>
        </>
      </RequestsToolbar>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {paginatedData.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-500 dark:text-gray-400">
              No {selectedFrame.toLowerCase()} requests available
            </p>
          </div>
        ) : (
          <Table className="w-full">
            <TableHeader className="border-gray-200 border-b-[3px] dark:border-gray-800">
              <TableRow>
                <TableCell className="font-light dark:text-gray-400 w-12 px-2 py-3">
                  <span></span>
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-2 pr-1 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-48"
                >
                  Guest Name
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-1 pr-1 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-32"
                >
                  Room Number
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-1 pr-2 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-30"
                >
                  Status
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-2 pr-4 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-30"
                >
                  Time Submitted
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-2 pr-2 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-40"
                >
                  Category
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y-[1px] divide-gray-200 dark:divide-gray-800">
              {paginatedData.map((request) => (
                <TableRow
                  key={request.requestId}
                  className={`${getRowClassName(
                    request.status,
                  )} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                    selectedRequest?.requestId === request.requestId
                      ? "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500"
                      : ""
                  }`}
                  onClick={() => handleSelectRequest(request)}
                >
                  <TableCell className="py-3 px-2 w-12">
                    <button
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        toggleStars(request.requestId);
                      }}
                    >
                      {starredList.has(request.requestId) ? (
                        <StarIconSolid className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <StarIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-2 pr-1 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-48 ${getFrameClass()}`}
                  >
                    {displayValue(request.guestName)}
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-1 pr-1 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-32 ${getFrameClass()}`}
                  >
                    {displayValue(request.room?.roomNumber)}
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-1 pr-2 w-30 min-w-[120px] ${getFrameClass()}`}
                  >
                    <div className="relative inline-block">
                      <button
                        ref={(el) => {
                          statusButtonRefs.current[request.requestId] = el;
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStatusDropdown(request.requestId);
                        }}
                        disabled={updatingStatus[request.requestId]}
                        className="flex items-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updatingStatus[request.requestId] ? (
                          <div className="flex items-center gap-2">
                            <LoadingSpinner size="sm" color="primary" />
                            <span>Updating...</span>
                          </div>
                        ) : (
                          <>
                            <span className="mr-2">
                              {getStatusDisplay(request.status) ===
                              "In Progress" ? (
                                <>
                                  <span className="hidden xsm:inline whitespace-nowrap">
                                    In Progress
                                  </span>
                                  <span className="inline xsm:hidden whitespace-nowrap">
                                    In Prog
                                  </span>
                                </>
                              ) : (
                                <span className="whitespace-nowrap">
                                  {getStatusDisplay(request.status)}
                                </span>
                              )}
                            </span>
                            <ChevronDownIcon className="w-4 h-4" />
                          </>
                        )}
                      </button>
                      {statusDropdowns[request.requestId] &&
                        !updatingStatus[request.requestId] && (
                          <Dropdown
                            isOpen={statusDropdowns[request.requestId]}
                            onClose={() =>
                              setStatusDropdowns((prev) => ({
                                ...prev,
                                [request.requestId]: false,
                              }))
                            }
                            className="w-36 z-50"
                            usePortal={true}
                            buttonRef={
                              {
                                current:
                                  statusButtonRefs.current[request.requestId],
                              } as React.RefObject<HTMLElement>
                            }
                          >
                            {getStatusOptions()
                              .filter(
                                (opt) =>
                                  opt !== getStatusDisplay(request.status),
                              )
                              .map((opt) => (
                                <DropdownItem
                                  key={opt}
                                  onItemClick={() => {
                                    handleTableStatusChange(
                                      request.requestId,
                                      opt,
                                    );
                                  }}
                                  className="dark:text-white"
                                >
                                  {opt}
                                </DropdownItem>
                              ))}
                          </Dropdown>
                        )}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-2 pr-4 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-30 ${getFrameClass()}`}
                  >
                    <SimpleDate date={request.createdAt} />
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-2 pr-2 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-40 ${getFrameClass()}`}
                  >
                    {displayValue(request.requestCategory)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Show</span>
          <select
            value={pagination.limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white pr-8"
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            items per page
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white dark:bg-gray-800"
          >
            Previous
          </button>
          <span className="text-sm dark:text-gray-400">
            Page {pagination.page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(pagination.page + 1)}
            disabled={!hasMore}
            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white dark:bg-gray-800"
          >
            Next
          </button>
        </div>
      </div>

      {/* Backdrop: click outside sidebar (including header/app nav) to close all sidebars */}
      {(selectedRequest !== null || showMessageHistory) && (
        <div
          aria-hidden
          className="fixed inset-0 z-40 top-0 left-0 right-0 bottom-0"
          onClick={() => {
            setShowMessageHistory(false);
            setSelectedRequest(null);
          }}
        />
      )}
      {/* SIDEBAR CONTAINER WITH ANIMATION */}
      <div
        className={`
          fixed right-0 top-[70px] w-[30%] h-[calc(100vh-70px)] z-50
          px-5 py-5 shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[0_0_15px_rgba(255,255,255,0.1)] 
          bg-white dark:bg-black overflow-y-auto 
          transition-transform duration-300 ease-in-out transform
          ${showMessageHistory ? "hidden" : ""}
          ${selectedRequest !== null ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {/* SIDEBAR CONTENT - Conditionally rendered */}
        {selectedRequest !== null && (
          <>
            {/* Navigation and Close Button */}
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (!selectedRequest) return;
                    const currentIndex = sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    );

                    // If we're at the first item of the page and not on page 1, go to previous page
                    if (currentIndex === 0 && pagination.page > 1) {
                      setPendingNavigation("previous");
                      setPage(pagination.page - 1);
                      // The sidebar will be cleared, and the new page data will load
                      // The selected request will be set to the last item of the new page
                      return;
                    }

                    // Otherwise, navigate within the current page
                    if (currentIndex > 0) {
                      handleSelectRequest(sortedData[currentIndex - 1]);
                    }
                  }}
                  disabled={
                    !selectedRequest ||
                    (sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    ) === 0 &&
                      pagination.page === 1)
                  }
                  className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous request"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    if (!selectedRequest) return;
                    const currentIndex = sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    );

                    // If we're at the last item of the page and there's a next page, go to next page
                    if (currentIndex === sortedData.length - 1 && hasMore) {
                      setPendingNavigation("next");
                      setPage(pagination.page + 1);
                      // The sidebar will be cleared, and the new page data will load
                      // The selected request will be set to the first item of the new page
                      return;
                    }

                    // Otherwise, navigate within the current page
                    if (currentIndex < sortedData.length - 1) {
                      handleSelectRequest(sortedData[currentIndex + 1]);
                    }
                  }}
                  disabled={
                    !selectedRequest ||
                    (sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    ) ===
                      sortedData.length - 1 &&
                      !hasMore)
                  }
                  className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next request"
                >
                  <ChevronLeftIcon className="w-5 h-5 rotate-180" />
                </button>
              </div>
              <button
                className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors"
                onClick={() => setSelectedRequest(null)}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Star & Timestamp */}
            <div className="flex items-center gap-5 mb-4">
              <button onClick={() => toggleStars(selectedRequest.requestId)}>
                {starredList.has(selectedRequest.requestId) ? (
                  <StarIconSolid className="w-5 h-5 text-yellow-500" />
                ) : (
                  <StarIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>
              <p className="font-light text-gray-500 dark:text-gray-300 italic">
                Submitted: <SimpleDate date={selectedRequest.createdAt} />
              </p>
            </div>

            {/* Status Dropdown */}
            <div className="flex items-center mb-4">
              <p className="font-medium text-black dark:text-white mr-2">
                Status:
              </p>
              <div className="relative">
                <button
                  onClick={() =>
                    setIsSidebarStatusDropdownOpen(!isSidebarStatusDropdownOpen)
                  }
                  className="flex items-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-gray-50 dark:bg-gray-700 text-black dark:text-white"
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner size="sm" color="primary" />
                      <span>Updating...</span>
                    </div>
                  ) : (
                    <>
                      <span className="mr-1">
                        {getStatusDisplay(selectedRequest.status)}
                      </span>
                      <ChevronDownIcon className="w-4 h-4" />
                    </>
                  )}
                </button>
                {isSidebarStatusDropdownOpen && (
                  <Dropdown
                    isOpen={isSidebarStatusDropdownOpen}
                    onClose={() => setIsSidebarStatusDropdownOpen(false)}
                    className="w-36"
                  >
                    {getStatusOptions().map((opt) => (
                      <DropdownItem
                        key={opt}
                        onItemClick={() =>
                          handleSidebarStatusChange(
                            opt
                              .toUpperCase()
                              .replace(/\s/g, "_") as RequestStatus,
                          )
                        }
                        className="dark:text-white"
                      >
                        {opt}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </div>
              {statusError && (
                <span className="ml-2 text-sm text-red-500">{statusError}</span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Note: The guest will be notified of this status change.
            </p>

            {/* Description */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                Description
              </h3>
              <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <p className="font-light text-black dark:text-white text-base leading-relaxed break-words">
                  {displayValue(
                    selectedRequest.description,
                    "No description provided.",
                  )}
                </p>
              </div>
            </div>

            {/* Room Details and Timing Information */}
            <div className="mb-4 space-y-3 text-sm">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                Room Details
              </h3>
              {/* Guest Name */}
              <div className="mb-4">
                <div className="flex items-start">
                  <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                    Guest Name:
                  </p>
                  <p className="font-light text-black dark:text-white break-words">
                    {displayValue(
                      selectedRequest.guestInfo?.name ||
                        selectedRequest.guestName,
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Room Number:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(selectedRequest.room?.roomNumber)}
                </p>
              </div>

              {selectedRequest.scheduledTime && (
                <div className="flex items-start pt-1">
                  <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                    Scheduled:
                  </p>
                  <p className="font-light text-black dark:text-white break-words">
                    {formatDateWithTimezone(
                      selectedRequest.scheduledTime,
                      hotelInfo?.timezone,
                    )}
                  </p>
                </div>
              )}
              {selectedRequest.completedAt && (
                <div className="flex items-start pt-1">
                  <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                    Completed:
                  </p>
                  <p className="font-light text-black dark:text-white break-words">
                    {formatDateWithTimezone(
                      selectedRequest.completedAt,
                      hotelInfo?.timezone,
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Live Status Field */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
                Send a Message
              </h3>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg">
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-0 focus:border-gray-300 dark:focus:border-gray-600 border-b border-gray-300 dark:border-gray-600 rounded-t-lg border-x-0 border-t-0"
                  placeholder="Enter subject (optional)..."
                />
                {/* Message Type Dropdown */}
                <div className="px-3 py-2 border-b border-gray-300 dark:border-gray-600">
                  <label
                    htmlFor="messageType"
                    className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Message Type
                  </label>
                  {isLoadingMessageTypes ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Loading types...
                    </p>
                  ) : messageTypeError ? (
                    <p className="text-sm text-red-500">
                      Error: {messageTypeError.message}
                    </p>
                  ) : (
                    <select
                      id="messageType"
                      value={selectedMessageTypeId}
                      onChange={(e) => setSelectedMessageTypeId(e.target.value)}
                      className="w-full bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none text-sm rounded-md p-1 border border-gray-300 dark:border-gray-600"
                      disabled={
                        messageTypes.length === 0 || !selectedRequest.guestId
                      }
                    >
                      {messageTypes.length === 0 && (
                        <option key="no-types" value="">
                          No types available
                        </option>
                      )}
                      {messageTypes.map((type) => (
                        <option key={type.typeid} value={type.typeid}>
                          {type.type}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <textarea
                  value={liveStatus}
                  onChange={(e) => setLiveStatus(e.target.value)}
                  rows={3}
                  className="w-full px-3 pt-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-0 border-0 rounded-b-lg"
                  placeholder="Enter message content (required)..."
                  disabled={isPushingStatus || !selectedRequest.guestId}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                  onClick={() => setShowMessageHistory(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600"
                >
                  View Message History
                </button>
                <button
                  onClick={handlePushToGuest}
                  disabled={isPushingStatus || !selectedRequest.guestId}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-[#1100D8] rounded-md hover:bg-[#0e00b8] dark:bg-white/10 dark:hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPushingStatus ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner size="sm" color="primary" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    "Send to Guest"
                  )}
                </button>
              </div>
              {showPushAlert && (
                <div className="mt-3">
                  <Alert
                    variant="success"
                    title=""
                    message="Message successfully sent to guest!"
                  />
                </div>
              )}
            </div>

            {/* Additional Sidebar Fields */}
            <div className="space-y-3 text-sm">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                Request Details
              </h3>

              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Request ID:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {selectedRequest.requestId}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Guest Email:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(
                    selectedRequest.guestInfo?.email ||
                      selectedRequest.guestContact?.email,
                  )}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Guest Phone:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(
                    selectedRequest.guestInfo?.phone ||
                      selectedRequest.guestContact?.phone,
                  )}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Category:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(selectedRequest.requestCategory)}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Department:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {getDepartmentDisplay(selectedRequest.department)}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onConfirm={handleConfirmSend}
        onCancel={handleCancelSend}
        title="Confirm Message"
        message={`Are you sure you want to send this message to the guest?${
          pendingMessage?.subject
            ? `\n\nSubject: ${pendingMessage.subject}`
            : ""
        }\n\nContent: ${pendingMessage?.content || ""}`}
        isLoading={isPushingStatus}
      />

      {/* Message History Sidebar */}
      <MessageHistorySidebar
        isOpen={showMessageHistory}
        onClose={() => {
          setShowMessageHistory(false);
          try {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete("requestId");
              return next;
            });
          } catch {
            // Ignore URL update errors
          }
        }}
        requestId={selectedRequest?.requestId}
      />

      {/* Status Update Success Toast */}
      {showStatusUpdateAlert && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <Alert
            variant="success"
            title="Status Updated"
            message="Request status has been updated successfully."
          />
        </div>
      )}
    </div>
  );
}
